import { CircularProgress } from '@mui/material'
import { useMemo, useState } from 'react'
import SingleExpandAccordionWrapper from 'src/consumer/components/common/accordion/SingleExpandAccordionWrapper'
import {
  APPLICABLE_TO_ENTITY,
  ENTITY_FEE_MODEL_APPLICABLE_TO_VALUES_ARRAY,
} from 'src/consumer/constants'
import FeeModelViewer from './FeeModelViewer'

const LEGACY_CLINIC_WIDE_MODEL_ID = 'legacy-clinic-wide-model'
const LEGACY_MENTOR_WIDE_MODEL_ID = 'legacy-mentor-wide-model'

function getNormalizedApplicableTypeId(applicableToId, applicableEntityType) {
  if (applicableToId) {
    return applicableToId
  }
  return applicableEntityType === APPLICABLE_TO_ENTITY.clinic.id
    ? LEGACY_CLINIC_WIDE_MODEL_ID
    : LEGACY_MENTOR_WIDE_MODEL_ID
}

function buildLegacyApplicableType(applicableToId, applicableEntityType) {
  if (!applicableToId) {
    return null
  }

  if (
    applicableToId === APPLICABLE_TO_ENTITY.clinic.id ||
    applicableToId === LEGACY_CLINIC_WIDE_MODEL_ID
  ) {
    return {
      id: applicableToId,
      title: 'Clinic-wide Model',
      titleForEntityModel: 'Legacy Clinic-wide Model',
      longDesc:
        'This is an older clinic-side engagement model that is not yet mapped to the newer practitioner buckets.',
      for: applicableEntityType,
    }
  }

  if (
    applicableToId === APPLICABLE_TO_ENTITY.mentor.id ||
    applicableToId === LEGACY_MENTOR_WIDE_MODEL_ID
  ) {
    return {
      id: applicableToId,
      title: 'Mentor-wide Model',
      titleForEntityModel: 'Legacy Mentor-wide Model',
      longDesc:
        'This is an older practitioner-side engagement model that is not yet mapped to the newer patient-source buckets.',
      for: applicableEntityType,
    }
  }

  return {
    id: applicableToId,
    title: applicableToId,
    titleForEntityModel: applicableToId,
    longDesc: 'This configured model uses an older or custom applicability value.',
    for: applicableEntityType,
  }
}

export default function FeeModelsDisplay({
  entityFeeModels,
  inApplicableEntityFeeModels,
  feeModels,
  feeModelGroups,
  forMentor = false,
  loading = false,
}) {
  const [expandedAccordion, setExpandedAccordion] = useState(null)
  const applicableEntityType = forMentor ? APPLICABLE_TO_ENTITY.mentor.id : APPLICABLE_TO_ENTITY.clinic.id

  const relevantApplicableTypes = useMemo(() => {
    const allModels = [...(entityFeeModels || []), ...(inApplicableEntityFeeModels || [])]
    const configuredApplicableTypeIds = Array.from(
      new Set(
        allModels
          .map((model) => getNormalizedApplicableTypeId(model?.applicableTo, applicableEntityType))
          .filter(Boolean),
      ),
    )
    const typesFromGroups =
      feeModelGroups
        ?.filter((group) => group?.applicableFor === applicableEntityType)
        ?.map((group) => ({
          ...group,
          titleForEntityModel: group?.titleForEntityFeeModel || group?.titleForEntityModel,
          for: applicableEntityType,
        })) || []

    const fallbackTypes = ENTITY_FEE_MODEL_APPLICABLE_TO_VALUES_ARRAY?.filter(
      (type) => type?.for === applicableEntityType,
    )

    const mergedTypes = new Map()
    fallbackTypes?.forEach((type) => {
      if (type?.id) {
        mergedTypes.set(type.id, type)
      }
    })
    typesFromGroups.forEach((type) => {
      if (type?.id) {
        mergedTypes.set(type.id, { ...mergedTypes.get(type.id), ...type })
      }
    })
    configuredApplicableTypeIds.forEach((id) => {
      if (!mergedTypes.has(id)) {
        const legacyType = buildLegacyApplicableType(id, applicableEntityType)
        if (legacyType?.id) {
          mergedTypes.set(legacyType.id, legacyType)
        }
      }
    })

    if (mergedTypes.size > 0) {
      return Array.from(mergedTypes.values())
    }

    return configuredApplicableTypeIds.map((id) => buildLegacyApplicableType(id, applicableEntityType))
  }, [applicableEntityType, entityFeeModels, feeModelGroups, inApplicableEntityFeeModels])

  const activeModelsByApplicableType = useMemo(() => {
    return (entityFeeModels || []).reduce((acc, model) => {
      const normalizedApplicableTypeId = getNormalizedApplicableTypeId(
        model?.applicableTo,
        applicableEntityType,
      )
      if (!acc[normalizedApplicableTypeId]) {
        acc[normalizedApplicableTypeId] = []
      }
      acc[normalizedApplicableTypeId].push(model)
      return acc
    }, {})
  }, [applicableEntityType, entityFeeModels])

  const inactiveModelsByApplicableType = useMemo(() => {
    return (inApplicableEntityFeeModels || []).reduce((acc, model) => {
      const normalizedApplicableTypeId = getNormalizedApplicableTypeId(
        model?.applicableTo,
        applicableEntityType,
      )
      if (!acc[normalizedApplicableTypeId]) {
        acc[normalizedApplicableTypeId] = []
      }
      acc[normalizedApplicableTypeId].push(model)
      return acc
    }, {})
  }, [applicableEntityType, inApplicableEntityFeeModels])

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center flex-grow">
        <CircularProgress />
      </div>
    )
  }

  return (
    <div
      className="d-flex flex-column gap-4 px-4"
      style={{ maxWidth: '968px', width: '100%', margin: '20px auto 10px' }}
    >
      {relevantApplicableTypes?.length > 0 && (
        <div className="d-flex flex-column gap-2">
          <h3 className="mb-0" style={{ fontWeight: 'bold' }}>
            Engagement Models
          </h3>
          <span style={{ color: '#6c757d' }}>
            Each clinic or practitioner bucket is shown separately. Older or custom configured
            models are also surfaced instead of being hidden.
          </span>
        </div>
      )}

      {relevantApplicableTypes?.map((applicableType) => {
        const activeModels = activeModelsByApplicableType?.[applicableType?.id] || []
        const inactiveModels = inactiveModelsByApplicableType?.[applicableType?.id] || []
        const inactiveAccordionId = `inactive-${applicableType?.id}`

        return (
          <div
            key={applicableType?.id}
            className="d-flex flex-column gap-3"
            style={{
              border: '1px solid #e9ecef',
              borderRadius: '12px',
              padding: '20px',
              backgroundColor: '#fff',
            }}
          >
            <div className="d-flex flex-column gap-1">
              <h4 className="mb-0" style={{ fontWeight: 700 }}>
                {applicableType?.titleForEntityModel || applicableType?.title}
              </h4>
              {!!applicableType?.longDesc && (
                <span style={{ color: '#6c757d' }}>{applicableType?.longDesc}</span>
              )}
            </div>

            {activeModels.length > 0 ? (
              <div className="d-flex flex-column gap-4">
                {activeModels.map((model, idx) => (
                  <FeeModelViewer
                    key={model?.id || `${applicableType?.id}-${idx}`}
                    allFeeModels={feeModels}
                    entityFeeModel={model}
                    modelNo={activeModels.length > 1 ? idx + 1 : undefined}
                    feeModelGroups={feeModelGroups}
                    applicableToObject={applicableType}
                    forMentor={forMentor}
                  />
                ))}
              </div>
            ) : (
              <FeeModelViewer
                allFeeModels={feeModels}
                entityFeeModel={{ applicableTo: applicableType?.id }}
                feeModelGroups={feeModelGroups}
                applicableToObject={applicableType}
                forMentor={forMentor}
                isPlaceholder
              />
            )}

            {inactiveModels.length > 0 && (
              <SingleExpandAccordionWrapper
                isExpanded={expandedAccordion === inactiveAccordionId}
                expandOnChange={() =>
                  setExpandedAccordion(
                    expandedAccordion === inactiveAccordionId ? null : inactiveAccordionId,
                  )
                }
                expandedItemIdentifier={inactiveAccordionId}
                accordionSummaryHeading={
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontWeight: 'bold' }}>
                      View {inactiveModels.length} Inactive Model
                      {inactiveModels.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                }
                accordionProps={{
                  style: {
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                  },
                }}
              >
                <div className="d-flex flex-column gap-4">
                  {inactiveModels.map((model, idx) => (
                    <FeeModelViewer
                      key={model?.id || `${applicableType?.id}-inactive-${idx}`}
                      allFeeModels={feeModels}
                      entityFeeModel={model}
                      modelNo={idx + 1}
                      feeModelGroups={feeModelGroups}
                      applicableToObject={applicableType}
                      forMentor={forMentor}
                      isInactive
                    />
                  ))}
                </div>
              </SingleExpandAccordionWrapper>
            )}
          </div>
        )
      })}

      {!relevantApplicableTypes?.length && !entityFeeModels?.length && !inApplicableEntityFeeModels?.length && (
        <div className="text-center mt-4">
          <h4 style={{ color: '#6c757d' }}>No fee models found</h4>
        </div>
      )}
    </div>
  )
}
